import React, {
  useMemo,
} from 'react';
import { Row } from 'antd';
import PropTypes from 'prop-types';

import ResourceDayCell from './ResourceDayCell';
import MonthShiftDayEntry from '../MonthlySchedule/MonthShiftDayEntry';
import MonthlyMoreView from '../MonthlySchedule/MonthlyMoreView';
import {
  BAR_GAP,
  BAR_HEIGHT,
  BAR_TOP,
  MORE_SLOT,
  getDaysInRange,
  layoutResourceRow,
} from './resourceRowLayout';
import { resourceAxisPropType, resourceRowPropType } from './resourceAxis.propTypes';

const AXIS_NAME_FONT_DEFAULT = 13;
const AXIS_NAME_FONT_MIN = 10;

/** Shrink axis label font for long names; pairs with 2-line clamp in schedule.less. */
const axisNameFontSize = (name = '') => {
  const len = name.length;
  if (len <= 18) return AXIS_NAME_FONT_DEFAULT;
  return Math.max(
    AXIS_NAME_FONT_MIN,
    AXIS_NAME_FONT_DEFAULT - Math.ceil((len - 18) / 6),
  );
};

export default function ResourceRow({
  axis,
  row,
  startDate,
  shifts,
  onShiftSelect,
  onShiftDrop,
  numDays,
  style,
  rowLayout,
}) {
  const { RowAvatar } = axis;

  const days = useMemo(
    () => getDaysInRange(startDate, numDays),
    [numDays, startDate],
  );

  const placedBars = useMemo(
    () => rowLayout || layoutResourceRow(row, shifts, days, axis.shiftField),
    [rowLayout, shifts, days, row, axis],
  );

  const axisNameStyle = useMemo(() => {
    if (!row?.name) return undefined;
    const fontSize = axisNameFontSize(row.name);
    return {
      fontSize,
      lineHeight: `${Math.round(fontSize * (18 / 13))}px`,
    };
  }, [row?.name]);

  return (
    <Row
      className="schedule-user-week-row"
      style={{ ...style, height: placedBars.rowHeight }}
    >
      <div className="schedule-user-week-row-user-name">
        {row && <RowAvatar row={row} className="schedule-user-axis-avatar" />}
        <div className="schedule-user-axis-person">
          <div
            className={`schedule-user-axis-name${row ? '' : ' schedule-user-axis-name-unassigned'}`}
            title={row?.name}
            style={axisNameStyle}
          >
            {row ? row.name : axis.unassignedLabel}
          </div>
          {row?.subtitle && (
            <div className="schedule-user-axis-role">{row.subtitle}</div>
          )}
        </div>
      </div>
      {days.map((dt, idx) => {
        const dayEntries = placedBars.entries.filter((entry) => entry.covered.includes(idx));
        const hidden = dayEntries.filter((entry) => !entry.visible);
        let addTop = BAR_TOP;
        dayEntries.forEach((entry) => {
          if (!entry.visible) return;
          const below = BAR_TOP + ((entry.lane + 1) * (BAR_HEIGHT + BAR_GAP));
          if (below > addTop) addTop = below;
        });
        if (hidden.length) addTop = Math.max(addTop, BAR_TOP + ((placedBars.visibleMaxLane + 1) * (BAR_HEIGHT + BAR_GAP)) + MORE_SLOT);
        return (
          <div className="schedule-user-axis-day" key={dt.toSQLDate()}>
            <ResourceDayCell
              axis={axis}
              row={row}
              day={dt}
              addTop={addTop}
              onShiftDrop={onShiftDrop}
            />
          </div>
        );
      })}
      <div className="schedule-user-shift-layer">
        {placedBars.entries.filter((entry) => entry.visible).map((entry) => (
          <MonthShiftDayEntry
            key={entry.shift.id}
            resourceId={row?.id}
            shift={entry.shift}
            onClick={onShiftSelect}
            userBar
            hoverCard
            hoverPlacement="rightTop"
            barStyle={{
              position: 'absolute',
              top: BAR_TOP + (entry.lane * (BAR_HEIGHT + BAR_GAP)),
              left: `calc(${entry.startIdx} * (100% / ${numDays}) + 4px)`,
              width: `calc(${entry.span} * (100% / ${numDays}) - 8px)`,
              height: BAR_HEIGHT,
              zIndex: 2,
            }}
          />
        ))}
        {days.map((dt, idx) => {
          const dayEntries = placedBars.entries.filter((entry) => entry.covered.includes(idx));
          const hiddenCount = dayEntries.filter((entry) => !entry.visible).length;
          if (!hiddenCount) return null;
          return (
            <div
              key={dt.toSQLDate()}
              className="schedule-user-more"
              style={{
                top: BAR_TOP + ((placedBars.visibleMaxLane + 1) * (BAR_HEIGHT + BAR_GAP)),
                left: `calc(${idx} * (100% / ${numDays}) + 4px)`,
                width: `calc((100% / ${numDays}) - 8px)`,
              }}
            >
              <MonthlyMoreView
                day={dt}
                moreCount={hiddenCount}
                interDayShifts={dayEntries.map((entry) => entry.shift)}
                onShiftSelect={onShiftSelect}
              />
            </div>
          );
        })}
      </div>
    </Row>
  );
}

/* eslint-disable react/forbid-prop-types */
ResourceRow.propTypes = {
  axis: resourceAxisPropType.isRequired,
  row: resourceRowPropType,
  startDate: PropTypes.object.isRequired,
  shifts: PropTypes.object.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
  onShiftDrop: PropTypes.func.isRequired,
  numDays: PropTypes.number.isRequired,
  style: PropTypes.object,
  rowLayout: PropTypes.shape({
    entries: PropTypes.array,
    visibleMaxLane: PropTypes.number,
    rowHeight: PropTypes.number,
  }),
};

ResourceRow.defaultProps = {
  row: undefined,
  style: undefined,
  rowLayout: undefined,
};
